/**
 * Phase 9 auth scaffolding, Phase 11.5 real-Auth0 wire-up.
 *
 * Tenant JWT lifecycle:
 *   1. UI obtains an Auth0 access token via one of two paths:
 *      - Real Auth0 (default, prod): the surrounding `<Auth0Provider>` runs
 *        `loginWithRedirect()` -> Auth0 Universal Login -> redirect back ->
 *        `getAccessTokenSilently()` returns a JWT signed by the Auth0 tenant.
 *      - Mock Auth0 (dev only, when `VITE_ENABLE_MOCK_AUTH0=true`): the UI
 *        calls `GET /dev/auth/login?email=...` to mint an Auth0-shaped JWT
 *        signed with a dev key. The API only mounts that route when
 *        `ENABLE_MOCK_AUTH0=true`; production deployments 404 it.
 *   2. UI exchanges via `POST /auth/exchange` for a Signatory JWT.
 *   3. The Signatory JWT lives in MEMORY ONLY (no localStorage). The Auth0
 *      SDK persists its own session via rotating refresh tokens / silent
 *      renew (`cacheLocation: 'memory'` by default — same trade-off, but
 *      `loginWithRedirect` re-establishes silently after a hard reload).
 *   4. When the Signatory JWT expires, callers re-run `getAccessTokenSilently`
 *      + `/auth/exchange` (no Signatory refresh token in v1).
 *
 * Runtime mode flip: `VITE_ENABLE_MOCK_AUTH0` is read at module load. No
 * rebuild is required to flip — it's a Vite env var that becomes a literal
 * at build time, BUT the build pipeline can produce either flavour and the
 * runtime code path is selected from the literal. The mock path is gated
 * by a single boolean, so it dead-code-eliminates cleanly in prod builds
 * that set the flag false.
 */
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface AuthAccount {
  id: string;
  slug: string;
  name?: string | null;
}

export interface AuthUser {
  id: string;
  email: string;
  role: 'owner' | 'member';
}

export interface AuthState {
  jwt: string | null;
  user: AuthUser | null;
  account: AuthAccount | null;
}

export interface AuthContextValue extends AuthState {
  /** True when the runtime is configured for the mock-Auth0 dev path. */
  mockEnabled: boolean;
  /** Mock-Auth0 dev login + exchange in one call (mock path only). */
  loginWithEmail(email: string): Promise<void>;
  /** Real Auth0 redirect (Universal Login). Real path only. */
  loginWithAuth0(): Promise<void>;
  /**
   * Called from the Auth0 callback once `isAuthenticated` flips true. Pulls
   * a fresh Auth0 access token via `getAccessTokenSilently()` and exchanges
   * it for a Signatory JWT. Safe to call repeatedly; idempotent on success.
   */
  completeAuth0Exchange(): Promise<void>;
  logout(): void;
  /**
   * Auth-injecting fetcher. Throws on non-2xx with a structured error so
   * react-query treats it as a query error.
   */
  fetcher<T = unknown>(path: string, init?: RequestInit): Promise<T>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export class ApiError extends Error {
  status: number;
  body: unknown;
  code: string | undefined;
  retryAfterMs: number | undefined;
  constructor(status: number, body: unknown) {
    super(extractMessage(body) ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
    const b = (body ?? {}) as Record<string, unknown>;
    this.code = typeof b['error'] === 'string' ? (b['error'] as string) : undefined;
    this.retryAfterMs =
      typeof b['retry_after_ms'] === 'number'
        ? (b['retry_after_ms'] as number)
        : undefined;
  }
}

function extractMessage(body: unknown): string | undefined {
  if (body && typeof body === 'object') {
    const m = (body as Record<string, unknown>)['message'];
    if (typeof m === 'string') return m;
  }
  return undefined;
}

/**
 * Vite exposes env vars on `import.meta.env`. We read the mock flag at
 * every login attempt (cheap; `import.meta.env` is a plain object); the
 * AuthProvider then selects between the mock and real branches. Default
 * = real Auth0 (prod-safe). Reading lazily lets tests `vi.stubEnv()` the
 * flag without `vi.resetModules()`.
 */
function isMockEnabled(): boolean {
  return (
    String(import.meta.env['VITE_ENABLE_MOCK_AUTH0'] ?? '').toLowerCase() ===
    'true'
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  const mockEnabled = isMockEnabled();
  const [state, setState] = useState<AuthState>({
    jwt: null,
    user: null,
    account: null,
  });
  // G2 — single-flight guard for silent renewal. Concurrent 401s share one
  // in-flight /auth/exchange instead of each firing its own (which could
  // self-inflict the rlAuthExchange 10/min 429).
  const renewRef = useRef<Promise<string | null> | null>(null);

  const exchangeAuth0Token = useCallback(
    async (accessToken: string): Promise<string> => {
      const exResp = await fetch('/auth/exchange', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: '{}',
      });
      if (!exResp.ok) {
        throw new ApiError(exResp.status, await safeJson(exResp));
      }
      const exBody = (await exResp.json()) as {
        token: string;
        account: AuthAccount;
        user: AuthUser;
      };
      setState({
        jwt: exBody.token,
        user: exBody.user,
        account: exBody.account,
      });
      return exBody.token;
    },
    [],
  );

  const loginWithEmail = useCallback(
    async (email: string) => {
      if (!mockEnabled) {
        throw new Error(
          'Mock-Auth0 login is disabled (VITE_ENABLE_MOCK_AUTH0!=true). Use loginWithAuth0().',
        );
      }
      const mockResp = await fetch(
        `/dev/auth/login?email=${encodeURIComponent(email)}`,
      );
      if (!mockResp.ok) {
        throw new ApiError(mockResp.status, await safeJson(mockResp));
      }
      const mockBody = (await mockResp.json()) as { access_token?: string };
      if (!mockBody.access_token) {
        throw new Error('mock-auth0 returned no access_token');
      }
      await exchangeAuth0Token(mockBody.access_token);
    },
    [mockEnabled, exchangeAuth0Token],
  );

  const loginWithAuth0 = useCallback(async () => {
    if (mockEnabled) {
      throw new Error(
        'Real Auth0 redirect disabled in mock mode (VITE_ENABLE_MOCK_AUTH0=true).',
      );
    }
    await auth0.loginWithRedirect();
  }, [mockEnabled, auth0]);

  const completeAuth0Exchange = useCallback(async () => {
    if (mockEnabled) return;
    if (!auth0.isAuthenticated) return;
    const accessToken = await auth0.getAccessTokenSilently();
    await exchangeAuth0Token(accessToken);
  }, [mockEnabled, auth0, exchangeAuth0Token]);

  // Silent-renew helper used by the fetcher on a 401 from `/api/*`. Pulls
  // a fresh Auth0 access token (rotating refresh token under the hood
  // when the SDK is configured with `useRefreshTokens: true`) and
  // re-exchanges for a Signatory JWT. Returns the new JWT, or `null` on
  // any failure — fetcher then falls through to today's "wipe state,
  // throw 401" behaviour. Skipped under mock-Auth0 (no refresh token).
  const silentRenew = useCallback(async (): Promise<string | null> => {
    if (mockEnabled) return null;
    if (!auth0.isAuthenticated) return null;
    // Coalesce concurrent renewals onto a single in-flight promise (G2).
    if (renewRef.current) return renewRef.current;
    const p = (async (): Promise<string | null> => {
      try {
        const accessToken = await auth0.getAccessTokenSilently();
        return await exchangeAuth0Token(accessToken);
      } catch {
        return null;
      } finally {
        renewRef.current = null;
      }
    })();
    renewRef.current = p;
    return p;
  }, [mockEnabled, auth0, exchangeAuth0Token]);

  const logout = useCallback(() => {
    // Clear in-memory Signatory state first so the UI bounces to login
    // immediately, then let Auth0 handle its own session teardown. The
    // mock path has no Auth0 SDK session, so the SDK call is a no-op
    // when mockEnabled is true (we skip it to avoid hitting Auth0).
    setState({ jwt: null, user: null, account: null });
    // G3 — drop all cached query data so a prior session's data (incl. PII)
    // can't flash on the next login in the same tab.
    queryClient.clear();
    if (!mockEnabled) {
      void auth0.logout({
        logoutParams: { returnTo: window.location.origin },
      });
    }
  }, [mockEnabled, auth0, queryClient]);

  const fetcher = useCallback(
    async <T,>(path: string, init?: RequestInit): Promise<T> => {
      const executeOnce = (bearer: string | null): Promise<Response> => {
        const headers = new Headers(init?.headers);
        if (bearer) headers.set('Authorization', `Bearer ${bearer}`);
        if (init?.body && !headers.has('Content-Type')) {
          headers.set('Content-Type', 'application/json');
        }
        return fetch(path, { ...init, headers });
      };

      let resp = await executeOnce(state.jwt);

      // 401 silent-renew (real-Auth0 path only). One retry max — if the
      // renewed JWT still 401s, fall through to the wipe-and-throw
      // branch below.
      if (resp.status === 401 && !mockEnabled) {
        const fresh = await silentRenew();
        if (fresh) resp = await executeOnce(fresh);
      }

      if (resp.status === 204) return undefined as T;
      const body = await safeJson(resp);
      if (!resp.ok) {
        // 401 -> clear state so the UI bounces to login.
        if (resp.status === 401) {
          setState({ jwt: null, user: null, account: null });
        }
        throw new ApiError(resp.status, body);
      }
      return body as T;
    },
    [state.jwt, mockEnabled, silentRenew],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      mockEnabled,
      loginWithEmail,
      loginWithAuth0,
      completeAuth0Exchange,
      logout,
      fetcher,
    }),
    [
      state,
      mockEnabled,
      loginWithEmail,
      loginWithAuth0,
      completeAuth0Exchange,
      logout,
      fetcher,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

async function safeJson(resp: Response): Promise<unknown> {
  try {
    return await resp.json();
  } catch {
    return undefined;
  }
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be inside <AuthProvider>');
  return ctx;
}
